// Button components with multiple styles.
import React, { useState } from "react";
import { DimensionValue, Image, StyleSheet, View } from "react-native";
import { colorAmbigousBlack, colorAmbiguousLightGrey, colorBorderFacepile, colorBorderPrimary, colorBorderSelected, colorButtonAccent, colorButtonDisabled, colorButtonPrimary, colorButtonSecondary, colorGreyHover, colorIconButtonPress, colorSurfaceInfo, colorSurfacePrimary, colorSurfaceSecondary, colorTextDisabled, colorTextHighlight, colorTextOnDarkBackground, colorTextPrimary, colorTextSecondary, colorTextSecondaryOnLightColorBg, colorTextWarning } from "./color";
import { DEPRECATED_EditorialHeading, Heading, Paragraph, TextType, TextWeight, UtilityText } from "./text";
import { HoverView, Pad, PadBox } from "./basics";
import { ChevronDown, ChevronUp, Close, Information, OverflowMenuHorizontal } from '@carbon/icons-react';
import { useTranslation } from "./translation";
import { undefA, undefC, undefE, undefN, undefS, t, undefTL, undefFP, makeTestID, labelToKey, TLabel } from "util/stdtypes";
import { hoverColor, pressColor } from "util/util";

export type ButtonType = 'primary' | 'secondary' | 'accent' | 'disabled' | 'delete';
export type ButtonSize = 'large' | 'compact' | 'small';

export function CTAButton({label = undefTL, text=undefS, testID=undefS, formatParams=undefFP, leftIcon=undefE, icon=undefE, type='primary' as ButtonType, disabled=false, size='large' as ButtonSize, wide=false, borderless=false, onPress=undefA}) {
    const s = CTAButtonStyle;
    
    const styleMap = {
        primary: {normal: s.primary, hover: s.primaryHover, pressed: s.primaryPressed, textColor: colorTextOnDarkBackground},
        secondary: {normal: s.secondary, hover: s.secondaryHover, pressed: s.secondaryPressed, textColor: colorTextPrimary},
        accent: {normal: s.accent, hover: s.accentHover, pressed: s.accentPressed, textColor: colorTextOnDarkBackground},
        disabled: {normal: s.disabled, hover: s.disabled, pressed: s.disabled, textColor: colorTextDisabled},
        delete: {normal: s.delete, hover: s.deleteHover, pressed: s.deletePressed, textColor: colorTextWarning}
    }
    const {normal, hover, pressed, textColor} = styleMap[disabled ? 'disabled' : type] ?? styleMap.primary;

    const sizeMap = {
        large: s.largeButton,
        compact: s.compactButton,
        small: s.smallButton,
    };

    return <HoverView disabled={disabled} role='button' testID={makeTestID({testID, label, text})}
        style={[sizeMap[size], wide && s.wide, normal, borderless && s.borderless]} hoverStyle={[s.hover, hover]}
        pressedStyle={pressed} onPress={onPress} >
        {leftIcon && <PadBox right={(label || text) ? (size === 'small' ? 6 : 8) : undefined}>{leftIcon}</PadBox>}
        <UtilityText type={size === 'small' ? 'tiny' : 'large'} label={label} text={text} formatParams={formatParams} color={textColor} />
        {icon && <PadBox left={(label || text) ? (size === 'small' ? 6 : 8) : undefined}>{icon}</PadBox>}
    </HoverView>
}

const CTAButtonStyle = StyleSheet.create({
    hover: {
    },
    primary: {
        backgroundColor: colorButtonPrimary,
        borderColor: colorButtonPrimary,
    },
    primaryHover: {
        backgroundColor: hoverColor(colorButtonPrimary),
        borderColor: hoverColor(colorButtonPrimary),
    },
    primaryPressed: {
        backgroundColor: pressColor(colorButtonPrimary),
        borderColor: pressColor(colorButtonPrimary),
    },
    secondary: {
        borderColor: colorBorderPrimary,
        backgroundColor: colorButtonSecondary,
    },
    secondaryPressed: {
        backgroundColor: pressColor(colorButtonSecondary),
    },
    secondaryHover: {
        backgroundColor: hoverColor(colorButtonSecondary),
    },
    accent: {
        backgroundColor: colorButtonAccent,
        borderColor: colorButtonAccent
    },
    accentPressed: {
        backgroundColor: pressColor(colorButtonAccent),
        borderColor: pressColor(colorButtonAccent),
    },
    accentHover: {
        backgroundColor: hoverColor(colorButtonAccent),
        borderColor: hoverColor(colorButtonAccent)
    },
    delete: {
        borderColor: colorBorderPrimary,
    },
    deletePressed: {
        backgroundColor: pressColor(colorSurfacePrimary),
        borderColor: colorBorderPrimary,
    },
    deleteHover: {
        backgroundColor: hoverColor(colorSurfacePrimary),
        borderColor: colorBorderPrimary,
    },
    disabled: {
        backgroundColor: colorButtonDisabled,
        borderColor: colorButtonDisabled,
        color: colorAmbiguousLightGrey
    },
    largeButton: {
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 32,
        alignSelf: 'flex-start',
        borderWidth: 1,
        flexDirection: 'row',
        height: 44,
        alignItems: 'center',
    },
    compactButton: {
        paddingHorizontal: 12,
        height: 40,
        borderRadius: 32,
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1
    },
    smallButton: {
        paddingHorizontal: 12,
        height: 32,
        borderRadius: 32,
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        borderWidth: 1
    },
    wide: {
        alignSelf: 'stretch',
        justifyContent: 'center',
        flex: 1
    },
    borderless: {
        borderWidth: 0
    }
})

export function ClickThroughTag({label = undefTL, text=undefS, emoji=undefS, onPress=undefA}) {
    return <CTAButton label={label} text={text} leftIcon={emoji ? <UtilityText text={emoji} 
        type="small" weight='medium' /> : undefined}
    type="secondary" size="small" borderless onPress={onPress} 
    />
}

export function IconButton({icon=undefC, onPress=undefA, iconProps={}, testID=undefS}) {
    const s = IconButtonStyle;
    return <HoverView testID={testID} style={s.button} hoverStyle={s.hover}
        pressedStyle={s.pressed} onPress={onPress} role='button'>
        {icon && React.createElement(icon, iconProps )} 
    </HoverView>
}
const IconButtonStyle = StyleSheet.create({
    button: {
        color: colorTextPrimary,
        height: 32,
        width: 32,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
        backgroundColor: colorSurfacePrimary,
    },
    hover: {
        backgroundColor: hoverColor(colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: pressColor(colorSurfacePrimary),
    }
});


export function ActionButton({label = undefTL, text=undefS, testID=undefS, 
    ariaLabel = undefTL, icon=undefA, iconProps={}, wide=false, onPress=undefA, 
    textColor=undefS, hover=false, pressed=false
}) {
    const s = ActionButtonStyle;
    return <HoverView 
        style={[s.button, wide ? s.wide : null, hover ? s.hover : null]} 
        hoverStyle={[s.hover]}
        ariaLabel={ariaLabel} testID={makeTestID({testID, ariaLabel, label, text})}
        pressedStyle={s.pressed} onPress={onPress} role='button'>
        {icon && React.createElement(icon, iconProps )} 
        {icon && (label || text) && <Pad size={8} />}
        <UtilityText label={label} text={text} color={textColor} /> 
    </HoverView>
}
const ActionButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        height: 32,
        alignItems: 'center',
        paddingHorizontal: 8,
        alignSelf: 'flex-start',
        borderRadius: 32,
        color: colorTextPrimary,
        backgroundColor: colorSurfacePrimary
    },
    wide: {
        alignSelf: 'stretch',
        justifyContent: 'center'
    },
    hover: {
        backgroundColor: hoverColor(colorSurfacePrimary),
    },
    pressed: {
        backgroundColor: pressColor(colorSurfacePrimary)
    }
});

export function SubtleButton({label = undefTL, text = undefS, ariaLabel = undefTL, testID = undefS, 
    disabled = false, formatParams = undefFP, color = colorTextSecondary, icon = undefC, iconProps = undefA, 
    padLeft = false, padRight = false, onPress = undefA
}) {
    const s = SubtleButtonStyle;
    const [hover, setHover] = useState(false);
    return <HoverView style={[s.button, padLeft && {marginLeft: 20}, padRight && {marginRight: 20}]}
        disabled={disabled} role={!disabled ? 'button' : undefined}
        ariaLabel={ariaLabel} testID={makeTestID({testID, ariaLabel, label, text})}
        onPress={onPress} setHover={setHover}>
        {icon && React.createElement(icon, {...iconProps, color})}
        {(label || text) && <Pad size={4} />}
        <UtilityText label={label} text={text} weight={'medium'} underline={hover}
            color={color} formatParams={formatParams} type='small' />
    </HoverView>
}
const SubtleButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
    }
});


export function TextButton({label = undefTL, text=undefS, weight='regular' as TextWeight, type='large' as TextType, heading=false, paragraph=false, editorial=false, underline=false, strong=false, italic=false, formatParams=undefFP, leftIcon=undefA, leftIconProps={}, rightIcon=undefA, rightIconProps={}, color=colorTextPrimary, alignStart=false, onPress=undefA}) {
    const s = TextButtonStyle;
    const [hover, setHover] = useState(false);
    return <HoverView shrink testID={makeTestID({label, text})}
        style={[s.button, alignStart ? {alignSelf: 'flex-start'} : null]}
        setHover={setHover} onPress={onPress} role='button'>
        {leftIcon && React.createElement(leftIcon, {...leftIconProps, color})}
        {leftIcon && <Pad size={8} />}
        {paragraph ?
            <Paragraph label={label} text={text} formatParams={formatParams} type={type}
                color={color} underline={hover != underline} strong={strong} />
            : editorial ?
                <DEPRECATED_EditorialHeading type={type} label={label} text={text} formatParams={formatParams}
                    color={color} underline={hover != underline} italic={italic} />
                : heading ?
                    <Heading label={label} text={text} formatParams={formatParams} type={type}
                        color={color} underline={hover != underline} weight={strong ? 'medium' : 'regular'} />
                    :
                    <UtilityText label={label} text={text} formatParams={formatParams} type={type}
                        color={color} underline={hover != underline} weight={strong ? 'medium' : 'regular'} />
        }
        {rightIcon && <Pad size={8} />}
        {rightIcon && React.createElement(rightIcon, {...rightIconProps, color})}
    </HoverView>
}
const TextButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
    }
})

export function TextLinkButton({label, text, type='large' as TextType, paragraph=false, editorial=false, underline=false, strong=false, italic=false, formatParams=undefFP, leftIcon=undefA, rightIcon=undefA, color=colorAmbigousBlack, alignStart=false, onPress=undefA}) {
    const [hover, setHover] = useState(false);
    return <HoverView shrink style={[alignStart ? {alignSelf: 'flex-start'} : null]} setHover={setHover} onPress={onPress} role='button' testID={makeTestID({label, text})}>
        {leftIcon && React.createElement(leftIcon, {color})}
        {leftIcon && <Pad size={8} />}
        {paragraph ?
            <Paragraph label={label} text={text} formatParams={formatParams} type={type}
                color={color} underline={hover != underline} strong={strong} />
            : editorial ?
                <DEPRECATED_EditorialHeading type={type} label={label} text={text} formatParams={formatParams}
                    color={color} underline={hover != underline} italic={italic} />
                :
                <UtilityText label={label} text={text} formatParams={formatParams} type={type}
                    color={color} underline={hover != underline} strong={strong} />
        }
        {rightIcon && <Pad size={8} />}
        {rightIcon && React.createElement(rightIcon, {color})}
    </HoverView>
}



export function ExpandButton({
    summaryElement=undefE,
    label = undefTL, text=undefS, type='tiny' as TextType,
    formatParams = undefFP, expanded=false, setExpanded=(e: boolean)=>{},
    testID = undefS,
}) {
    const s = ExpanderButtonStyle;
    const [hover, setHover] = useState(false);

    const tLabel = useTranslation(label, formatParams);
    const ariaText = useTranslation(expanded ? '{text} - hide' : '{text} - show', {text: tLabel ?? text});

    return <HoverView style={s.button} setHover={setHover} role='button' ariaText={ariaText}
        onPress={() => setExpanded(!expanded)} testID={makeTestID({testID, label, text})}>
        {summaryElement && <PadBox right={4}>{summaryElement}</PadBox>}
        <UtilityText label={label} text={text} formatParams={formatParams} type={type}
            underline={hover} weight='medium' aria-hidden/>
        <Pad size={4} />
        {expanded ? <ChevronUp size={20} color={colorTextPrimary}/> : <ChevronDown size={20} color={colorTextPrimary}/>}
    </HoverView>

}
const ExpanderButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignItems: 'center',
    }
})

export function Tag({label = undefTL, emoji = undefS, text = undefS, type='emphasized', 
    strong=false, formatParams=undefFP, color=undefS, compact=false, dashed=false, 
    borderColor=undefS, overrideTinyTextColor = false, textColor = colorTextSecondaryOnLightColorBg, maxWidth='100%'
}) {
    const s = TagStyle;
    return <View style={[
        s.button,
        type == 'emphasized' && s.emphasized,
        type == 'tiny' && s.tiny,
        color ? {borderColor: borderColor || color, backgroundColor: color} : null,
        compact && {height: 28},
        dashed && {borderStyle: 'dashed'},
        {maxWidth: maxWidth as DimensionValue}
    ]}
    >
        {emoji && <PadBox right={6}><UtilityText text={emoji} type='tiny' weight='medium' /></PadBox>}
        <UtilityText color={type =='tiny' && !overrideTinyTextColor ? colorTextHighlight : textColor}
            label={label} text={text} formatParams={formatParams}
            type='tiny' weight={strong ? 'strong' : 'medium'}
            numberOfLines={1}
        />
    </View>
}
const TagStyle = StyleSheet.create({
    button: {
        alignSelf: 'flex-start',
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderWidth: 1,
        borderRadius: 4,
        height: 32,
        // borderColor: 'red',
        borderColor: colorBorderPrimary
    },
    emphasized: {
        borderRadius: 100,
    },
    tiny: {
        paddingHorizontal: 6,
        paddingVertical: 2,
        borderRadius: 100,
        height: 20,
        backgroundColor: colorSurfaceInfo,
        borderColor: colorSurfaceInfo
    }
})

export function BetaTag({type='primary'}) {
    return <View style={[BetaTagStyle[type], BetaTagStyle.container]}>
        <PadBox horiz={8} vert={2.5}>
            <UtilityText type='tiny' caps weight='medium' label={t('Beta')} color={colorTextOnDarkBackground} />
        </PadBox>
    </View>
}

const BetaTagStyle = StyleSheet.create({
    container: {
        borderRadius: 32,
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'row',
        alignSelf: 'flex-start',
    },
    primary: {
        backgroundColor: colorButtonAccent
    },
    secondary: {
        backgroundColor: 'transparent', 
        borderColor: colorBorderFacepile, 
        borderWidth: 1
    }
})



export function ReactionButton({emoji=undefS, icon=undefC, wide=false, iconProps={color: colorTextPrimary}, viewOnly=false, label = undefTL, text=undefS, count=undefN, selected=false, onPress=undefA}){
    const s = ReactionButtonStyle;
    return <HoverView style={[s.horiz, !viewOnly && s.button, selected && s.pressed, wide && s.wide]}
        hoverStyle={s.hover} disabled={viewOnly} testID={makeTestID({label, text})}
        pressedStyle={s.pressed} role='button' onPress={onPress}>
        {icon && React.createElement(icon, iconProps )} 
        {emoji && <UtilityText aria-hidden text={emoji} type='small' weight='medium' />}
        {(label || text) ? <UtilityText label={label} text={text} type='small' weight='medium'/> : null}
        {count ? <UtilityText text={count.toString()} type='small' weight='medium' color={colorTextWarning} /> : null}
    </HoverView>
}
const ReactionButtonStyle = StyleSheet.create({
    horiz: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    viewOnly: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    wide: {
        flex: 1,
        alignSelf: 'stretch',
        justifyContent: 'center',
    },
    button: {
        flexDirection: 'row',
        alignSelf: 'flex-start',
        height: 40,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
        backgroundColor: colorSurfacePrimary,
    },
    hover: {
        backgroundColor: colorGreyHover,
    },
    pressed: {
        borderColor: colorBorderSelected,
        backgroundColor: colorSurfaceSecondary
    }
})

export function FilterButton({emoji=undefS, label = undefTL, text=undefS, count=undefN, selected=false, onPress=undefA}) {
    const s = FilterButtonStyle;
    const [pressed, setPressed] = useState(false);

    return <HoverView style={[s.button, selected && s.selected]} pressedStyle={s.pressed} hoverStyle={s.hover}
        onPress={onPress} setPressed={setPressed} testID={makeTestID({label, text})}>
        {emoji && <PadBox right={8}><UtilityText text={emoji} type='small' weight='medium' /></PadBox>}
        <UtilityText label={label} text={text} type='small' weight='medium'/>
        {count ? <Pad size={8} /> : null}
        <UtilityText text={count ? count.toString() : ''} type='small' weight='medium' color={colorTextWarning} />
    </HoverView>
}
const FilterButtonStyle = StyleSheet.create({
    button: {
        height: 32,
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'flex-start',
        borderColor: colorBorderPrimary,
        borderWidth: 1,
        borderRadius: 100,
        paddingHorizontal: 12,
        backgroundColor: colorSurfacePrimary
    },
    hover: {
        backgroundColor: hoverColor(colorSurfacePrimary),
    },
    pressed: {
        borderColor: colorBorderPrimary,
        backgroundColor: pressColor(colorSurfacePrimary),
    },
    selected: {
        borderColor: colorBorderSelected,
    }
});


export function BannerIconButton({type, testID=undefS, onPress}) {
    const s = BannerIconButtonStyle;
    const icon = type == 'close' ? Close : Information;
    return <HoverView style={s.regular} hoverStyle={s.hover}
        pressedStyle={s.pressed} onPress={onPress} testID={testID}>
        {React.createElement(icon, {size: 24, color: colorTextPrimary})}
    </HoverView>
}

const BannerIconButtonStyle = StyleSheet.create({
    regular: {
        width: 24,
        height: 24,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    hover: {
        backgroundColor: colorGreyHover
    },
    pressed: {
        backgroundColor: colorIconButtonPress
    }
})


export function BreadCrumb({ icon=undefC, ariaLabel = undefTL, testID=undefS, iconProps = {}, onPress=undefA }) {

    const s = BreadCrumbStyle;
    return <HoverView
        style={s.regular} hoverStyle={s.hover} role='button'
        pressedStyle={s.pressed} onPress={onPress} testID={testID} ariaLabel={ariaLabel}>
        {icon && React.createElement(icon, iconProps)}
    </HoverView>
}

const BreadCrumbStyle = StyleSheet.create({
    regular: {
        width: 32,
        height: 32,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
    },
    hover: {
        backgroundColor: hoverColor(colorSurfacePrimary)
    },
    pressed: {
        backgroundColor: pressColor(colorSurfacePrimary)
    }
})

export function PhotoPile({photoUrlList}) {
    const s = PhotoPileStyle;
    if (photoUrlList.length == 0) return null;
    if (photoUrlList.length == 1) {
        return <Image style={s.singlePhoto} source={{uri: photoUrlList[0]}} />
    } else if (photoUrlList.length == 2) {
        return <View style={s.outer}>
            <Image style={[s.twoPhoto, {left: 0, top: 0}]} source={{uri: photoUrlList[0]}} />
            <Image style={[s.twoPhoto, {right: 0, bottom: 0}]} source={{uri: photoUrlList[1]}} />
        </View>
    } else if (photoUrlList.length == 3) {
        return <View style={s.outer}>
            <Image style={[s.threePhoto, {left: 0, top: 0}]} source={{uri: photoUrlList[0]}} />
            <Image style={[s.threePhoto, {left: 6, top: 6}]} source={{uri: photoUrlList[1]}} />
            <Image style={[s.threePhoto, {right: 0, bottom: 0}]} source={{uri: photoUrlList[2]}} />
        </View>
    }
}
const PhotoPileStyle = StyleSheet.create({
    outer: {
        width: 32,
        height: 32
    },
    singlePhoto: {
        width: 32,
        height: 32,
        marginRight: 4
    },
    twoPhoto: {
        position: 'absolute',
        width: 24,
        height: 24,
        borderWidth: 2,
        borderColor: colorBorderFacepile
    },
    threePhoto: {
        position: 'absolute',
        width: 20,
        height: 20,
        borderWidth: 2,
        borderColor: colorBorderFacepile
    },
})
